interface ProductLike {
  id: string | number;
  slug?: string | null;
  [key: string]: unknown;
}

interface CategoryLike {
  products?: ProductLike[] | null;
  [key: string]: unknown;
}

// Backend slugs are `name.replace(/\s+/g, "-").toLowerCase()` with no further
// sanitisation, so an en-dash in the dish name survives as U+2013 between two
// ASCII hyphens. Chrome / copy-paste often fold that to `---`. `%xx` sequences
// show up on refresh because the browser encodes the UTF-8 slug in the request.
const UNICODE_DASH_RE = /[\u2010-\u2015\u2212\uFE58\uFE63\uFF0D]/g;

export function normaliseProductSlug(slug: string): string {
  let decoded = slug;
  try {
    decoded = decodeURIComponent(slug);
  } catch {
    // Malformed % sequence — compare the raw value.
  }
  return decoded.toLowerCase().replace(UNICODE_DASH_RE, "-");
}

/**
 * Resolve a product by slug **within one maker's categories**.
 *
 * Scoped deliberately: `Product.slug` is unique per *maker*, not globally, so
 * `GET /api/public/products/slugs/:slug` could return a different maker's product
 * with the same slug. The maker detail payload already carries every product with
 * its slug, so resolve locally instead of adding a request.
 *
 * Pass the maker's raw `productCategories` (not the display-filtered store list).
 * `setProductCategories` drops inactive categories, but Set Menus still render
 * active bundles from those categories — looking at the filtered list 404s their
 * canonical URL on refresh.
 *
 * The same product object is pushed into every category it belongs to, so the first
 * match is returned and duplicates are ignored. Matching is case-insensitive
 * because the backend's uniqueness check uses `Op.like`.
 */
export function findProductBySlug(
  productCategories: unknown,
  slug: string | undefined | null,
): ProductLike | null {
  if (!slug || !Array.isArray(productCategories)) return null;

  const target = normaliseProductSlug(slug);

  for (const category of productCategories as CategoryLike[]) {
    const products = category?.products;
    if (!Array.isArray(products)) continue;

    for (const product of products) {
      if (typeof product?.slug === "string" && normaliseProductSlug(product.slug) === target) {
        return product;
      }
    }
  }

  return null;
}

/**
 * Resolve a product by id within one maker's categories.
 *
 * Used to upgrade a legacy `?product={id}` link to its canonical path without an
 * extra request — the maker payload already carries every product's id and slug.
 * Ids are compared as strings because the route query is always a string while
 * `Product.id` is numeric.
 */
export function findProductById(
  productCategories: unknown,
  id: string | number | undefined | null,
): ProductLike | null {
  if (id === undefined || id === null || id === "" || !Array.isArray(productCategories)) {
    return null;
  }

  const target = String(id);

  for (const category of productCategories as CategoryLike[]) {
    const products = category?.products;
    if (!Array.isArray(products)) continue;

    for (const product of products) {
      if (product && String(product.id) === target) return product;
    }
  }

  return null;
}
